import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Access, nearestAllowedRealm } from 'global/components/droprealms';
import Form from 'global/components/form';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { get, size } from 'global/lib/lodash';
import setUserRealm from 'redux/actions/userRealm';

const exceptBracketMatch = (string) => string?.replace(/\[.*?\]/, '');

const firstBracketMatch = (...strings) => {
  for (const str of strings) {
    const match = str?.match(/\[(.*?)\]/);
    if (match) return match[1];
  }
  return undefined;
};

export const defaultAssessmentName = (realm, template) => {
  const tempCopy = firstBracketMatch(get(template, 'name'));
  const name = get(realm, 'name') || exceptBracketMatch(get(template, 'name'));
  const suffix = get(realm, 'tier') === 'lot' ? tempCopy || exceptBracketMatch(get(template, 'name'))?.trim() : tempCopy;
  return `${name || ''}${suffix ? ` - ${suffix}` : ''}`;
};

const realmFromSel = (sel) => {
  const id = Object.keys(sel || {})[0];
  return id ? sel[id] : undefined;
};

const seedSelection = (currentRealm, template, onlyTiers) => {
  const r = nearestAllowedRealm(currentRealm || get(template, 'realm'), onlyTiers);
  return r ? { [r.id]: r } : {};
};

// Location picker + optional lot-level title/assignee step. Create stays in the
// action bar; Next shows when lots are in play (only allowed tier, viewing lots,
// or a lot is selected) and becomes Back after continuing.
export default function useTemplateLocationSteps({ template, currentRealm, onlyTiers, onCancel, onSubmit, disabled }) {
  const dispatch = useDispatch();
  const templateId = get(template, 'id');
  const [step, setStep] = useState('location');
  const [pendingSel, setPendingSel] = useState({});
  const [selRealm, setSelRealm] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [lotFormState, setLotFormState] = useState({});
  const [viewingRealm, setViewingRealm] = useState();

  useEffect(() => {
    setStep('location');
    setSelRealm();
    setIsEditing(false);
    setLotFormState({});
    setViewingRealm();
    setPendingSel(templateId ? seedSelection(currentRealm, template, onlyTiers) : {});
    // Seed only when the chosen template changes; currentRealm is read at that time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const pendingRealm = realmFromSel(pendingSel);
  const hasPending = !!get(pendingRealm, 'id');
  const needsLotInfo = get(pendingRealm, 'tier') === 'lot';
  const isLotStep = step === 'lotInfo';
  const lotRealmId = needsLotInfo ? get(pendingRealm, 'id') : get(selRealm, 'id');
  const lotIsOnlyAllowedTier = size(onlyTiers) > 0 && onlyTiers.every((t) => t === 'lot');
  const viewingLots = get(viewingRealm, 'tier') === 'community' || get(viewingRealm, 'children.0.tier') === 'lot';
  const showNext = !isLotStep && (needsLotInfo || viewingLots || lotIsOnlyAllowedTier);

  const users = useRetrieveData(
    { url: '/users' },
    {
      useEffect: true,
      waitFor: !!lotRealmId,
      build: ['users'],
      params: { filter: { content_realm_id: lotRealmId } },
      useCache: { reload: 1 },
    }
  );

  const canCreate =
    !disabled &&
    !isEditing &&
    hasPending &&
    (!needsLotInfo || (!!get(lotFormState, 'reportName') && size(get(lotFormState, 'assignees')) > 0));

  const goToLotInfo = () => {
    if (!needsLotInfo || disabled || isEditing) return;
    setSelRealm(pendingRealm);
    setStep('lotInfo');
  };

  const createAssessment = async () => {
    if (!canCreate) return;
    const r = needsLotInfo ? selRealm || pendingRealm : pendingRealm;
    // Site location is independent of create succeeding — set it on Create so a
    // failed POST still leaves the user in the chosen location to retry.
    dispatch(setUserRealm(r));
    await onSubmit(r, needsLotInfo ? lotFormState : {});
  };

  const buttons = [{ title: 'Cancel', variant: 'text', disabled, onClick: onCancel }];
  if (showNext) {
    buttons.push({ title: 'Next', variant: 'outlined', disabled: disabled || isEditing || !needsLotInfo, onClick: goToLotInfo });
  }
  if (isLotStep) {
    buttons.push({ title: 'Back', variant: 'outlined', disabled, onClick: () => setStep('location') });
  }
  buttons.push({ title: 'Create', disabled: !canCreate, onClick: createAssessment });

  const content = isLotStep ? (
    <Form
      onFormState={setLotFormState}
      showAllScreens
      screens={[
        {
          inputs: [
            {
              id: 'reportName',
              type: 'TextField',
              defaultValue: () => get(lotFormState, 'reportName') || defaultAssessmentName(selRealm, template),
              props: {
                label: 'Title',
                required: true,
              },
            },
            {
              id: 'assignees',
              type: 'UserSelect',
              defaultValue: () => get(lotFormState, 'assignees') || null,
              data: () => users.data,
              props: {
                label: 'Assignee',
                required: true,
                multiple: false,
              },
            },
          ],
        },
      ]}
    />
  ) : (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Access
        onlyTiers={onlyTiers}
        singleSelect
        canAdd
        disableListScroll
        selected={pendingSel}
        onSelectedChange={(sel) => {
          const next = sel || {};
          if (get(realmFromSel(next), 'id') !== get(pendingRealm, 'id')) {
            setLotFormState({});
          }
          setPendingSel(next);
        }}
        onCurrentRealm={setViewingRealm}
        onEditingChange={setIsEditing}
        openToSelected
        currentRealm={selRealm || currentRealm || get(template, 'realm')}
        openToChildren
      />
    </Box>
  );

  return { content, buttons, step };
}
